import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { HierarchyFilterComponent } from '../../../components/FilterBar/HierarchyFilter';
import { UsageAnalyticsFilterComponent } from '../../../components/UsageAnalyticsFilter';
import { MetricBox } from '../../../components/MetricBox';
import { DemographicsChart } from '../../../components/charts/DemographicsChart';
import { HierarchyFilter, UsageAnalyticsData, UsageAnalyticsFilter } from '../../../lib/types';
import { subDays } from 'date-fns';

export function UsageAnalytics() {
  const { brandId } = useParams<{ brandId: string }>();
  const [user, setUser] = useState<any>(null);
  const [filter, setFilter] = useState<HierarchyFilter>({
    dateFrom: subDays(new Date(), 30).toISOString(),
    dateTo: new Date().toISOString(),
    granularity: 'day',
    compareToPrevious: false,
    brandScope: 'sku_or_campaign',
  });

  const [data, setData] = useState<UsageAnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [usageFilter, setUsageFilter] = useState<UsageAnalyticsFilter>({
    productIds: [],
    checkoutTimeRangeId: undefined
  });

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      fetch('/api/auth/me', {
        headers: { 'Authorization': `Bearer ${token}` }
      })
      .then(res => res.json())
      .then(data => {
        setUser(data.user);
      });
    }
  }, []);

  useEffect(() => {
    if (user?.tenantId) {
      fetchUsageData();
    }
  }, [user, filter, usageFilter]);

  const fetchUsageData = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        dateFrom: filter.dateFrom,
        dateTo: filter.dateTo,
        granularity: filter.granularity,
        compareToPrevious: String(filter.compareToPrevious || false),
        brandScope: filter.brandScope || 'sku_or_campaign',
      });

      // Add usage filter parameters
      if (usageFilter.productIds.length > 0) {
        usageFilter.productIds.forEach(id => params.append('productIds', id));
      }
      if (usageFilter.checkoutTimeRangeId) {
        params.append('checkoutTimeRangeId', usageFilter.checkoutTimeRangeId);
      }

      const token = localStorage.getItem('token');
      const response = await fetch(`/api/brand/${user.tenantId}/dashboard/usage?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch usage data');
      }

      const data = await response.json();
      setData(data);
    } catch (error) {
      console.error('Error fetching usage data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleExportCSV = () => {
    console.log('Export CSV');
  };

  const handleExportPNG = () => {
    console.log('Export PNG');
  };

  const handleExportPagePDF = () => {
    console.log('Export Page PDF');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-lg text-gray-600">Loading...</div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-lg text-red-600">Failed to load data</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Filter Section with proper separation */}
      <div className="bg-white shadow-sm">
        <HierarchyFilterComponent
          filter={filter}
          onChange={setFilter}
          onExportCSV={handleExportCSV}
          onExportPNG={handleExportPNG}
          onExportPagePDF={handleExportPagePDF}
          showBrandScope={true}
        />
      </div>

      {/* Main Content with proper spacing */}
      <div className="max-w-7xl mx-auto p-6 pt-8">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Usage Analytics</h1>
          <p className="text-gray-600">Understand footfall patterns, user demographics, and behavior analytics</p>
        </div>

        {/* Usage Analytics Filter */}
        <UsageAnalyticsFilterComponent
          filter={usageFilter}
          onFilterChange={setUsageFilter}
          products={data?.products || []}
          checkoutTimeRanges={data?.checkoutTimeRanges || []}
        />

        {/* Footfall Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
          <MetricBox title="Footfall" value={data.footfall.total} format="number" />
          <MetricBox title="Home" value={data.footfall.home} format="number" />
          <MetricBox title="By Category" value={data.footfall.byCategory} format="number" />
          <MetricBox title="By Product" value={data.footfall.byProduct} format="number" />
          <MetricBox title="Average Check-Out Time" value={data.footfall.averageCheckoutTime} format="number" decimals={1} subtitle="seconds" />
        </div>

        {/* Demographics Charts */}
        <div className="mb-8">
          <DemographicsChart data={data.demographics} />
        </div>

        {/* Additional Usage Insights */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white p-6 rounded-lg shadow">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Footfall Patterns</h3>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Peak Hours</span>
                <span className="text-sm font-semibold text-gray-900">12:00-14:00, 17:00-19:00</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Peak Days</span>
                <span className="text-sm font-semibold text-gray-900">Tuesday, Thursday, Friday</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Conversion Rate</span>
                <span className="text-sm font-semibold text-green-600">19.1%</span>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">User Behavior Insights</h3>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Average Session Duration</span>
                <span className="text-sm font-semibold text-gray-900">2.3 minutes</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Return Visitor Rate</span>
                <span className="text-sm font-semibold text-gray-900">34.2%</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Mobile vs Desktop</span>
                <span className="text-sm font-semibold text-gray-900">68% / 32%</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
